import { useCallback, useEffect, useState } from "react";

import { apiClient } from "@/features/shared";

const INITIAL_VARIANT_VALUES = {
  variantAttribute1Value: null,
  variantAttribute2Value: null,
  variantAttribute3Value: null,
  variantAttribute4Value: null,
};

export const useProductVariantPicker = () => {
  const [product, setProductState] = useState(null);
  const [variantValues, setVariantValues] = useState(INITIAL_VARIANT_VALUES);

  const [attributeMeta, setAttributeMeta] = useState(null);
  const [loadingAttrs, setLoadingAttrs] = useState(false);

  const [productVariant, setProductVariant] = useState(null);
  const [loadingVariant, setLoadingVariant] = useState(false);

  const setProduct = useCallback((newProduct) => {
    setAttributeMeta(null);
    setProductState(newProduct);
    setProductVariant(null);
    setVariantValues(INITIAL_VARIANT_VALUES);
  }, []);

  const setVariantValue = useCallback((index, value) => {
    setVariantValues((prev) => ({
      ...prev,
      [`variantAttribute${index + 1}Value`]: value,
    }));
  }, []);

  const reset = useCallback(() => {
    setProductState(null);
    setVariantValues(INITIAL_VARIANT_VALUES);
    setAttributeMeta(null);
    setProductVariant(null);
  }, []);

  const fetchAttributeMeta = useCallback(async () => {
    setAttributeMeta(null);
    setLoadingAttrs(true);
    try {
      const {
        data: { data },
      } = await apiClient.get(
        `products/${product.id}/product-variant-attribute-values`
      );
      setAttributeMeta(data);
    } finally {
      setLoadingAttrs(false);
    }
  }, [product]);

  const fetchProductVariant = useCallback(async () => {
    setProductVariant(null);
    setLoadingVariant(true);
    try {
      const {
        data: { data },
      } = await apiClient.get(`products/${product.id}/product-variants`, {
        params: {
          variantAttribute1Value: variantValues.variantAttribute1Value,
          variantAttribute2Value: variantValues.variantAttribute2Value,
          variantAttribute3Value: variantValues.variantAttribute3Value,
          variantAttribute4Value: variantValues.variantAttribute4Value,
        },
      });
      if (data.length > 0) setProductVariant(data[0]);
    } finally {
      setLoadingVariant(false);
    }
  }, [product, variantValues]);

  useEffect(() => {
    if (product) fetchAttributeMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    if (!attributeMeta) return;

    if (attributeMeta.variantAttribute1 && !variantValues.variantAttribute1Value)
      return;
    if (attributeMeta.variantAttribute2 && !variantValues.variantAttribute2Value)
      return;
    if (attributeMeta.variantAttribute3 && !variantValues.variantAttribute3Value)
      return;
    if (attributeMeta.variantAttribute4 && !variantValues.variantAttribute4Value)
      return;

    fetchProductVariant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    attributeMeta,
    variantValues.variantAttribute1Value,
    variantValues.variantAttribute2Value,
    variantValues.variantAttribute3Value,
    variantValues.variantAttribute4Value,
  ]);

  return {
    product,
    setProduct,
    variantValues,
    setVariantValue,
    attributeMeta,
    productVariant,
    loading: { attrs: loadingAttrs, variant: loadingVariant },
    reset,
  };
};
